import { gql } from '@apollo/client';
import { useOffsetPaginatedQuery } from '@everlutionsk/ui-apollo';
import { Typography } from '@mui/material';
import { StatusBadge } from '../components/coreComponents/badge';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/coreComponents/button';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { Table } from '../components/microLayouts/table';

// Lists previously-deleted tenants with restorable S3 backups. Paginated
// via the shared Table component + useOffsetPaginatedQuery — matches the
// rest of the asign UI's table pattern. Auto-polls every 3s while any
// row is `restoring` so the inline status chips stay live during a
// restore without forcing the admin to refresh.
export function Recovery() {
  const navigate = useNavigate();
  const { data, pagination, loading, refetch } = useOffsetPaginatedQuery(query, {
    variables: page => ({ input: { page } }),
    total: data => data.deletedAccountMany.total
  });

  const items = data?.deletedAccountMany.items ?? [];
  const hasInflight = items.some(row => row.status === 'restoring');

  useEffect(() => {
    if (!hasInflight) return;
    const interval = setInterval(() => {
      void refetch();
    }, 3000);
    return () => clearInterval(interval);
  }, [hasInflight, refetch]);

  return (
    <MainWrapper>
      <MainWrapperHeader
        title="Recovery"
        description="Restore previously-deleted tenants from the S3 backup. The owner gets a fresh temporary password; all other users keep their citadel records but need credentials re-issued from the user overview tab."
        primaryAction={
          <Button variant="secondary" onClick={() => void refetch()}>
            Refresh
          </Button>
        }
      />

      <Table
        items={loading ? undefined : items}
        pagination={pagination}
        rowKey={row => row.accountId}
        rowProps={row => ({
          onClick: () => navigate(`/recovery/${row.accountId}`),
          sx: { cursor: 'pointer' }
        })}
        columns={[
          {
            label: 'Alias',
            render: row => <Typography variant="body2">{row.alias ?? '—'}</Typography>
          },
          {
            label: 'Account ID',
            render: row => (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {row.accountId}
              </Typography>
            )
          },
          {
            label: 'Owner',
            render: row => <Typography variant="body2">{row.ownerEmail}</Typography>
          },
          {
            label: 'Deleted at',
            render: row => (
              <Typography variant="body2">{new Date(row.deletedAt).toLocaleString()}</Typography>
            )
          },
          {
            label: 'Status',
            render: row => <StatusChip status={row.status} />
          },
          {
            label: 'Restored at',
            render: row => (
              <Typography variant="body2">
                {row.restoredAt != null ? new Date(row.restoredAt).toLocaleString() : '—'}
              </Typography>
            )
          }
        ]}
        noDataPlaceholder="No deleted accounts."
      />
    </MainWrapper>
  );
}

function StatusChip({ status }: { readonly status: string }) {
  // `expired` is the only non-actionable state — the S3 dump is gone and
  // the row cannot be restored. Surface it in red with an explicit label
  // so admins don't try clicking through expecting it to work.
  if (status === 'expired') {
    return <StatusBadge tone="error" label="expired — not recoverable" />;
  }
  const color =
    status === 'restored'
      ? 'success'
      : status === 'failed'
        ? 'error'
        : status === 'restoring'
          ? 'info'
          : 'default';
  return <StatusBadge tone={color} label={status} />;
}

const query = gql<DeletedAccountManyGQL>`
  query DeletedAccountMany($input: DeletedAccountManyInput) {
    deletedAccountMany(input: $input) {
      total
      items {
        accountId
        alias
        ownerEmail
        deletedAt
        restoredAt
        status
      }
    }
  }
`;
